import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-bootstrap/Modal";
import crossBlack from "../../../../assets/images/onboardingImages/crossBlack.svg";
import longArrow from "../../../../assets/images/onboardingImages/longArrow.svg";
import BubbleSvgButton from "../../../sharedPages/CommonComponents/BubbleSvgButton/BubbleSvgButton";
import InfoPopup from "../../../sharedPages/CommonComponents/InfoPopup/InfoPopup";
import TradeMarketSegment from "../../../sharedPages/CommonComponents/TradeMarketSegment/TradeMarketSegment";
import "../CommunityName/CommunityName.css";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../../helpers/toast/toastify";
import {
  resetOnBoardingStartContext,
  storeUser,
  updateUser,
} from "../../../../redux/users/usersSlice";
import { fetchUser } from "../../../../helpers/helper";
import { getUserTradePermissionType } from "../../../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import {
  SPOT_DISPLAY_COIN_OPTIONS,
  normalizeSpotDisplayCoin,
  persistSpotDisplayCoin,
} from "../../../sharedPages/newSetting/SpotLiveTradeSettings/spotLiveTradeHideBelow";
import CloseModal from "../CreateSubscription/CloseModal";
import "./SelectSpotCoin.css";

const SPOT_COIN_SEGMENT_OPTIONS = SPOT_DISPLAY_COIN_OPTIONS.map((coin) => ({
  key: coin,
  label: coin,
}));

const SpotCoinSegment = ({ value, onChange }) => (
  <TradeMarketSegment
    options={SPOT_COIN_SEGMENT_OPTIONS}
    value={value}
    onChange={onChange}
    label=""
    hint=""
    ariaLabel="Spot quote coin"
  />
);

const SelectSpotCoin = () => {
  const api = useApi();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const onBoardingStartContext = useSelector(
    (state) => state.user?.onBoardingStartContext
  );

  const [show, setShow] = useState(true);
  const [warn, setWarn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState("USDT");

  const permissionType = getUserTradePermissionType(user);
  const needsSpotCoin =
    permissionType === "spot" || permissionType === "both";

  useEffect(() => {
    // New-community onboarding defaults to USDT — don't inherit stale localStorage
    // from a previous community / browser session.
    const fromUser = user?.spotQuotePreference;
    setSelectedCoin(
      fromUser ? normalizeSpotDisplayCoin(fromUser) : "USDT"
    );
  }, [user]);

  useEffect(() => {
    if (user && !needsSpotCoin) {
      navigate("/leader/onboarding/subscription", { replace: true });
    }
  }, [user, needsSpotCoin, navigate]);

  const handleCloseClick = () => {
    setWarn(true);
  };

  const handleClose = () => {
    if (onBoardingStartContext) {
      setShow(false);
      dispatch(resetOnBoardingStartContext());
      navigate(onBoardingStartContext, { replace: true });
      return;
    }
    navigate("/leader/dashboard", { replace: true });
    setShow(false);
  };

  const handleBack = () => {
    navigate("/leader/onboarding/connection", { replace: true });
  };

  const handleNext = async () => {
    const coin = normalizeSpotDisplayCoin(selectedCoin || "USDT");
    // Don't carry over a leftover hide-below from localStorage on this slide
    const hideBelow = 0;

    setIsLoading(true);
    try {
      const response = await api("post", "users/spotLiveDisplaySettings", {
        spotHideAssetsBelowUsd: hideBelow,
        spotQuotePreference: coin,
      });

      const ok =
        response?.status === true ||
        response?.success === true ||
        response?.status === "success";

      if (!ok) {
        toastify(
          response?.message ||
            response?.error ||
            "Failed to save coin preference.",
          "error"
        );
        return;
      }

      persistSpotDisplayCoin(coin);

      if (response?.data?.user) {
        dispatch(updateUser(response.data.user));
      } else if (user) {
        dispatch(
          updateUser({
            ...user,
            spotHideAssetsBelowUsd: hideBelow,
            spotQuotePreference: coin,
          })
        );
      }

      await fetchUser(api, dispatch, storeUser);
      navigate("/leader/onboarding/subscription", { replace: true });
    } catch (error) {
      toastify(error?.message || "Failed to save coin preference.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!needsSpotCoin) return null;

  return (
    <div className="onboarding-modal-wrapper">
      <Modal
        animation={false}
        show={show}
        backdropClassName="onboarding-custom-backdrop"
        centered
        dialogClassName="custom-onboarding-modal selectSpotCoin"
      >
        <Modal.Body className="figma-drop-shadow">
          <button className="reverseArrow" onClick={handleBack} type="button">
            <img src={longArrow} alt="" />
          </button>
          <button type="button" className="CrossIcon" onClick={handleCloseClick}>
            <img src={crossBlack} alt="" />
          </button>

          <div className="onboardingContentBox">
            <h2 className="title">
              <span>What coin do you use?</span>
              <InfoPopup
                contentItems={[
                  { text: "Spot quote coin", type: "heading" },
                  {
                    text: "This is the quote coin used for your spot live trades. Members copying your spot trades will follow this preference.",
                    type: "paragraph",
                  },
                ]}
              />
            </h2>
            <p className="desc">Members will follow this preference</p>

            <div className="formBox">
              <div className="mb-20px">
                <SpotCoinSegment
                  value={selectedCoin}
                  onChange={setSelectedCoin}
                />
              </div>

              <BubbleSvgButton
                className="NextBtn"
                onClick={handleNext}
                disabled={isLoading}
                isLoading={isLoading}
                text="Next"
                type="button"
                variant="black"
                containerStyling={{
                  justifyContent: "center",
                }}
                mainDivStyle={{
                  width: "275px",
                  height: "55px",
                }}
              />
            </div>
          </div>

          <div className="paginationDots">
            <button className="dot" type="button" />
            <button className="dot" type="button" />
            <button className="dot active" type="button" />
            <button className="dot" type="button" />
            <button className="dot" type="button" />
            <button className="dot" type="button" />
            <button className="dot" type="button" />
            <button className="dot" type="button" />
          </div>
        </Modal.Body>
      </Modal>

      {warn && (
        <CloseModal handleClose={handleClose} setWarn={setWarn} warn={warn} />
      )}
    </div>
  );
};

export default SelectSpotCoin;
